// Reckonings (docs/04 §16, docs/05 §5): every 5th window a Collector attaches a rule. The first one
// of a run is always The Auditor; after that they are drawn without repeats until the pool is spent.
// Tribute 40 becomes The Last Supper once the Cathedral is built; Endless stacks two rules every 3.
import { B } from '../content/balance';
import type { CollectorDef } from '../content/defs';
import { COLLECTOR_LIST, COLLECTORS } from '../content/registry';
import type { ScriptId } from '../content/dialogue';
import { sayScript } from './dialogue';
import type { Modifiers } from './modifiers';
import { stream, type Rng } from './rng';
import type { GameEvent, GameState, RunState } from './types';

export function newReckoningTrack(): RunState['reck'] {
  return { seen: [], upcoming: {}, beaten: 0 };
}

/** Is window n a Reckoning? */
export function isReckoning(n: number, mods: Pick<Modifiers, 'reckonings' | 'lastSupper'>, endless = false): boolean {
  if (!mods.reckonings || n <= 0) return false;
  if (n > B.LAST_SUPPER_N && endless) return (n - B.LAST_SUPPER_N) % B.ENDLESS_RECKONING_EVERY === 0;
  return n % B.RECKONING_EVERY === 0;
}

const isEndless = (run: RunState): boolean => (run.flags.endless ?? 0) > 0;

function drawCollector(run: RunState, rng: Rng, exclude: string[] = []): string {
  if (run.reck.seen.length === 0 && !exclude.includes(B.FIRST_COLLECTOR)) return B.FIRST_COLLECTOR;
  const pool = COLLECTOR_LIST.filter((c) => !c.final && !exclude.includes(c.id));
  let fresh = pool.filter((c) => !run.reck.seen.includes(c.id));
  if (fresh.length === 0) {
    // Pool spent: start over, but never the one we just met.
    const last = run.reck.seen[run.reck.seen.length - 1];
    run.reck.seen = [];
    fresh = pool.filter((c) => c.id !== last);
  }
  return rng.pick(fresh.length ? fresh : pool).id;
}

/** The collector for Reckoning window n: pre-drawn for previews, then remembered. */
export function collectorFor(run: RunState, mods: Modifiers, n: number): string {
  if (n === B.LAST_SUPPER_N && mods.lastSupper && !isEndless(run)) return 'lastsupper';
  const known = run.reck.upcoming[n];
  if (known) return known;
  const id = drawCollector(run, stream(run.rng, 'events'));
  run.reck.upcoming[n] = id;
  run.reck.seen.push(id);
  return id;
}

/** Upcoming Reckonings within the Omen range, drawn now so the preview is exact. */
export function reckoningPreview(run: RunState, mods: Modifiers): { n: number; id: string }[] {
  const out: { n: number; id: string }[] = [];
  for (let k = 1; k <= mods.omenWindows; k++) {
    const n = run.window.n + k;
    if (isReckoning(n, mods, isEndless(run))) out.push({ n, id: collectorFor(run, mods, n) });
  }
  return out;
}

/** Collector rules in force right now (their `mods` apply through computeModifiers). */
export function activeRules(run: RunState | null): CollectorDef[] {
  const ids = run?.window.rules ?? [];
  return ids.map((id) => COLLECTORS.get(id)).filter((d): d is CollectorDef => !!d);
}

export function reckoningDef(run: RunState | null): CollectorDef | undefined {
  return run?.window.reckoning ? COLLECTORS.get(run.window.reckoning) : undefined;
}

/**
 * Sets up the window's collector before its tribute and spins are computed. Returns the tribute
 * multiplier for the window (1 when it is not a Reckoning).
 */
export function setupReckoning(state: GameState, mods: Modifiers, n: number): number {
  const run = state.run!;
  delete run.window.reckoning;
  delete run.window.rules;
  if (!isReckoning(n, mods, isEndless(run))) return 1;
  const id = collectorFor(run, mods, n);
  const def = COLLECTORS.get(id)!;
  run.window.reckoning = id;
  if (def.rollsRules) run.window.rules = [];
  else if (isEndless(run)) {
    const second = drawCollector(run, stream(run.rng, 'events'), [id, 'twins']);
    run.window.rules = [id, second];
  } else run.window.rules = [id];
  if (!state.meta.almanac.collectors.includes(id)) state.meta.almanac.collectors.push(id);
  return def.tributeMult ?? mods.reckoningTributeMult;
}

/** Announces the Reckoning (after the window has its tribute and spins). */
export function announceReckoning(state: GameState, events: GameEvent[]): void {
  const run = state.run!;
  const def = reckoningDef(run);
  if (!def) return;
  events.push({ e: 'RECKONING_START', id: def.id, n: run.window.n });
  if (def.split) run.window.counters.twinsAt = Math.max(1, Math.floor(run.window.spinsTotal / 2));
  sayScript(state, `collector.${def.id}` as ScriptId, events, 'suspicious');
  if (def.rollsRules) rollNextRule(state, events);
}

/** The Last Supper: pick the rule for the next pull and show it before the pull. */
export function rollNextRule(state: GameState, events: GameEvent[]): void {
  const run = state.run!;
  const def = reckoningDef(run);
  if (!def?.rollsRules) return;
  const pool = COLLECTOR_LIST.filter((c) => c.perSpin);
  const next = stream(run.rng, 'events').pick(pool).id;
  run.window.rules = [next];
  events.push({ e: 'RULE_NEXT', rules: [next] });
}

/** Does any active rule split the tribute (The Twins)? */
export function splitRule(run: RunState): boolean {
  return activeRules(run).some((r) => r.split);
}

/** Does any active rule hex every spin (The Hex Widow)? */
export function hexEachSpin(run: RunState): boolean {
  return activeRules(run).some((r) => r.hexEachSpin);
}
